import { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowRight, ChevronLeft, ChevronRight, Heart } from "lucide-react";
import { api } from "../lib/api";
import { cldSquare } from "../lib/img";
import { useWishlist } from "../context/WishlistContext";

const TARGET_CATEGORIES = ["moissanite-watches", "cuban-chains", "grillz", "pendants"];

const CATEGORY_LABEL = {
  "moissanite-watches": "MOISSANITE",
  "cuban-chains": "925 SILVER",
  grillz: "18K GOLD",
  pendants: "925 SILVER",
};

function ProductCard({ product, index }) {
  const { isWishlisted, toggle } = useWishlist();
  const liked = isWishlisted(product.slug);
  const salePct =
    product.compare_at_price && product.compare_at_price > product.price
      ? Math.round(((product.compare_at_price - product.price) / product.compare_at_price) * 100)
      : 0;
  const label = CATEGORY_LABEL[product.category_slug] || "VVS LAB";

  return (
    <div
      className="group relative flex h-full flex-col border-2 border-black bg-white transition-transform duration-300 ease-out will-change-transform hover:-translate-y-1 hover:shadow-[8px_8px_0_#1e40af]"
      data-testid={`featured-card-${product.slug}`}
    >
      {/* badges */}
      <div className="absolute left-3 top-3 z-10 flex gap-1.5">
        <span className="bg-black px-2 py-1 text-[9px] font-black uppercase tracking-widest text-white">
          In Stock
        </span>
        {salePct > 0 && (
          <span
            className="bg-[#dc2626] px-2 py-1 text-[9px] font-black uppercase tracking-widest text-white"
            data-testid={`featured-sale-badge-${product.slug}`}
          >
            Sale {salePct}%
          </span>
        )}
      </div>

      {/* wishlist */}
      <button
        type="button"
        onClick={(e) => {
          e.preventDefault();
          toggle(product.slug);
        }}
        aria-label={liked ? "Remove from wishlist" : "Add to wishlist"}
        data-testid={`featured-wishlist-${product.slug}`}
        className="absolute right-3 top-3 z-10 flex h-8 w-8 items-center justify-center bg-white transition-colors duration-200 hover:bg-black hover:text-white"
      >
        <Heart size={15} className={liked ? "fill-[#1e40af] text-[#1e40af]" : ""} />
      </button>

      <Link to={`/${product.category_slug}/${product.slug}`} className="block">
        <div className="relative aspect-square overflow-hidden bg-gradient-to-br from-zinc-50 to-zinc-200">
          <img
            src={cldSquare(product.images?.[0], 600)}
            alt={product.name}
            loading="lazy"
            decoding="async"
            className="h-full w-full object-cover transition-transform duration-500 ease-out group-hover:scale-105"
          />
        </div>
      </Link>

      <div className="flex flex-1 flex-col p-4">
        <div className="flex items-center justify-between">
          <span className="text-[10px] font-bold uppercase tracking-[0.25em] text-zinc-500">{label}</span>
          <div className="flex gap-1">
            <span className="h-2.5 w-2.5 rounded-full border border-black/20 bg-zinc-300" />
            <span className="h-2.5 w-2.5 rounded-full border border-black/20 bg-[#d4af37]" />
          </div>
        </div>
        <Link
          to={`/${product.category_slug}/${product.slug}`}
          className="mt-2 line-clamp-2 min-h-[42px] font-display text-sm font-bold leading-tight text-black transition-colors duration-200 hover:text-[#1e40af]"
          data-testid={`featured-title-${product.slug}`}
        >
          {product.name}
        </Link>
        <div className="mt-auto flex flex-wrap items-baseline gap-x-2 gap-y-0.5 pt-3 tabular-nums">
          <span className="text-base font-black text-[#1e40af] sm:text-lg" data-testid={`featured-price-${product.slug}`}>
            ${Math.round(product.price)}
          </span>
          {product.compare_at_price ? (
            <span className="text-xs text-zinc-400 line-through sm:text-sm">${Math.round(product.compare_at_price)}</span>
          ) : null}
        </div>
      </div>
    </div>
  );
}

function DealsPromo() {
  return (
    <Link
      to="/cuban-chains"
      data-testid="featured-deals-promo"
      className="group relative flex h-full min-h-[420px] flex-col justify-between overflow-hidden border-2 border-black bg-gradient-to-br from-[#0b1a4b] via-[#1e40af] to-[#4c1d95] p-8 text-white lg:min-h-full"
    >
      <div className="pointer-events-none absolute inset-0 opacity-30 mix-blend-overlay" style={{ backgroundImage: "radial-gradient(circle at 20% 10%, rgba(255,255,255,0.4), transparent 40%), radial-gradient(circle at 80% 90%, rgba(255,255,255,0.25), transparent 45%)" }} />
      <div className="pointer-events-none absolute inset-0" style={{ backgroundImage: "url('data:image/svg+xml;utf8,<svg xmlns=%22http://www.w3.org/2000/svg%22 width=%22120%22 height=%22120%22><filter id=%22n%22><feTurbulence baseFrequency=%220.9%22/></filter><rect width=%22120%22 height=%22120%22 filter=%22url(%23n)%22 opacity=%220.12%22/></svg>')" }} />
      <div className="relative">
        <p className="text-[10px] font-bold uppercase tracking-[0.4em] text-white/70">Limited Drop</p>
        <p className="mt-6 font-display text-[11px] font-bold uppercase tracking-[0.4em] text-white/90">Best</p>
        <h3 className="mt-1 font-display text-6xl font-black leading-none tracking-tighter text-white sm:text-7xl">
          Deals
        </h3>
        <p className="mt-6 max-w-[220px] text-sm leading-relaxed text-white/80">
          Iced-out drops up to 40% off. Cuban chains, moissanite watches and custom sets — this week only.
        </p>
      </div>

      <div className="relative mt-8 flex items-center gap-4">
        <span className="flex h-14 w-14 items-center justify-center bg-white text-[#1e40af] transition-transform duration-300 group-hover:translate-x-2">
          <ArrowRight size={22} />
        </span>
        <span className="text-xs font-bold uppercase tracking-[0.3em] text-white/90">Shop Now</span>
      </div>

      <div className="pointer-events-none absolute -right-16 top-1/2 h-64 w-64 -translate-y-1/2 rounded-full bg-white/10 opacity-70" />
    </Link>
  );
}

export function FeaturedCollections() {
  const [featuredList, setFeaturedList] = useState([]);
  const [newList, setNewList] = useState([]);
  const [tab, setTab] = useState("featured");
  const trackRef = useRef(null);

  useEffect(() => {
    // Fetch each pool directly from the API so we don't depend on hardcoded
    // category slugs (they diverge between preview and production). If the
    // admin hasn't flagged anything as featured yet we still fall back to the
    // newest products so the section is never empty.
    Promise.all([
      api.get("/products", { params: { featured: true, limit: 12 } }),
      api.get("/products", { params: { limit: 12 } }),
    ]).then(([featRes, newRes]) => {
      const feat = Array.isArray(featRes.data) ? featRes.data : featRes.data?.products || [];
      const newest = Array.isArray(newRes.data) ? newRes.data : newRes.data?.products || [];
      setFeaturedList(feat.length ? feat : newest.slice(0, 10));
      setNewList(newest.slice(0, 10));
    }).catch(() => {
      setFeaturedList([]);
      setNewList([]);
    });
  }, []);

  const items = tab === "new" ? newList : featuredList;

  useEffect(() => {
    if (trackRef.current) trackRef.current.scrollLeft = 0;
  }, [tab]);

  const scroll = (dir) => {
    const el = trackRef.current;
    if (!el) return;
    const card = el.querySelector("[data-featured-card]");
    const step = (card?.getBoundingClientRect().width || 280) + 16;
    el.scrollBy({ left: dir * step, behavior: "smooth" });
  };

  const tabBtn = (id, label, testid) => (
    <button
      key={id}
      type="button"
      onClick={() => setTab(id)}
      data-testid={testid}
      role="tab"
      id={`featured-tab-btn-${id}`}
      aria-selected={tab === id}
      aria-controls={`featured-tabpanel-${id}`}
      tabIndex={tab === id ? 0 : -1}
      className={`border-2 px-5 py-2 text-[11px] font-bold uppercase tracking-[0.25em] transition-colors duration-200 ${
        tab === id
          ? "border-black bg-black text-white"
          : "border-black/20 bg-white text-black hover:border-black"
      }`}
    >
      {label}
    </button>
  );

  return (
    <section className="border-y-2 border-black bg-gradient-to-b from-white to-zinc-50" data-testid="featured-collections">
      <div className="mx-auto max-w-7xl px-4 py-16 sm:px-8 lg:py-24">
        <div className="flex flex-wrap items-end justify-between gap-4">
          <div>
            <p className="text-xs font-bold uppercase tracking-[0.4em] text-[#1e40af]">Featured Collections</p>
            <h2 className="mt-3 font-display text-3xl font-black tracking-tighter text-black sm:text-4xl">
              Iced Out. Hand Picked.
            </h2>
          </div>
        </div>

        <div className="mt-8 flex flex-wrap gap-2" role="tablist" aria-label="Featured collections" data-testid="featured-tabs">
          {tabBtn("featured", "Featured", "featured-tab-featured")}
          {tabBtn("new", "New Arrivals", "featured-tab-new")}
        </div>

        <div
          className="mt-8 grid gap-4 lg:grid-cols-[minmax(0,280px)_1fr] lg:items-stretch"
          role="tabpanel"
          id={`featured-tabpanel-${tab}`}
          aria-labelledby={`featured-tab-btn-${tab}`}
        >
          <div className="lg:h-full">
            <DealsPromo />
          </div>

          <div className="relative min-w-0">
            {/* Desktop: 5 x 2 grid */}
            <div className="hidden gap-4 lg:grid lg:grid-cols-5 lg:grid-rows-2" data-testid="featured-grid">
              {items.slice(0, 10).map((p, i) => (
                <ProductCard key={`${tab}-${p.slug}`} product={p} index={i} />
              ))}
            </div>

            {/* Mobile / tablet: horizontal snap carousel with floating arrows */}
            <div className="relative lg:hidden">
              <div
                ref={trackRef}
                className="hide-scrollbar -mx-4 flex snap-x snap-mandatory gap-4 overflow-x-auto scroll-smooth px-4 pb-2"
                data-testid="featured-track"
              >
                {items.slice(0, 10).map((p, i) => (
                  <div
                    key={`m-${tab}-${p.slug}`}
                    data-featured-card
                    className="w-[75%] shrink-0 snap-start sm:w-[46%] md:w-[32%]"
                  >
                    <ProductCard product={p} index={i} />
                  </div>
                ))}
              </div>

              <button
                type="button"
                onClick={() => scroll(-1)}
                aria-label="Previous"
                data-testid="featured-prev-btn"
                className="absolute left-1 top-1/2 z-20 flex h-11 w-11 -translate-y-1/2 items-center justify-center border-2 border-black bg-white text-black shadow-[3px_3px_0_#000] transition-all duration-200 active:translate-x-0.5 active:translate-y-0.5 active:shadow-none"
              >
                <ChevronLeft size={20} />
              </button>
              <button
                type="button"
                onClick={() => scroll(1)}
                aria-label="Next"
                data-testid="featured-next-btn"
                className="absolute right-1 top-1/2 z-20 flex h-11 w-11 -translate-y-1/2 items-center justify-center border-2 border-black bg-black text-white shadow-[3px_3px_0_#1e40af] transition-all duration-200 active:translate-x-0.5 active:translate-y-0.5 active:shadow-none"
              >
                <ChevronRight size={20} />
              </button>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
